const toTitleCase = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!str) return '';
  return str
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
    .join(' ');
};

const normalizeStatusKey = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  const str = String(value).trim();
  if (!str) return null;
  return str.replace(/[\s-]+/g, '_').toUpperCase();
};

const ENROLLMENT_STATUS_MAP = {
  ENROLLED: { label: 'Enrolled', color: 'success' },
  ACTION_REQUIRED: { label: 'Action Required', color: 'warning' },
  OPTED_OUT: { label: 'Opted Out', color: 'error' },
  OPT_OUT_PENDING: { label: 'Opt Out Pending', color: 'warning' },
  IN_PROGRESS: { label: 'In Progress', color: 'info' },
  PENDING_PROVIDER_CHANGE: { label: 'Pending Provider Change', color: 'warning' },
  COOLING: { label: 'Cooling', color: 'warning' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
  CANCELED: { label: 'Cancelled', color: 'error' },
  TERMINATED: { label: 'Terminated', color: 'error' },
};

const API_PRESENCE_STATUS_MAP = {
  PRESENT: { label: 'Present', color: 'success' },
  COOLING: { label: 'Cooling', color: 'warning' },
  MISSING_FROM_API: { label: 'Missing From API', color: 'warning' },
};

const PRODUCT_SELECTION_STATUS_MAP = {
  COMPLETE: { label: 'Complete', color: 'success' },
  INCOMPLETE: { label: 'Incomplete', color: 'warning' },
};

const CONSENT_STATUS_MAP = {
  COMPLETE: { label: 'Complete', color: 'success' },
  INCOMPLETE: { label: 'Incomplete', color: 'warning' },
};

const HAS_LIVE_SELECTIONS_MAP = {
  TRUE: { label: 'Yes', color: 'success' },
  FALSE: { label: 'No', color: 'error' },
};

const IS_BILLING_STATUS_MAP = {
  TRUE: { label: 'Billing', color: 'success' },
  FALSE: { label: 'Not Billing', color: 'default' },
  YES: { label: 'Billing', color: 'success' },
  NO: { label: 'Not Billing', color: 'default' },
  Y: { label: 'Billing', color: 'success' },
  N: { label: 'Not Billing', color: 'default' },
};

const IS_TERMINATED_STATUS_MAP = {
  TRUE: { label: 'Terminated', color: 'error' },
  FALSE: { label: 'Active', color: 'success' },
  YES: { label: 'Terminated', color: 'error' },
  NO: { label: 'Active', color: 'success' },
  Y: { label: 'Terminated', color: 'error' },
  N: { label: 'Active', color: 'success' },
};

const PRODUCT_SELECTION_LIFECYCLE_MAP = {
  LIVE: { label: 'Live', color: 'success' },
  PENDING_LIVE: { label: 'Pending Live', color: 'warning' },
  PENDING_CANCEL: { label: 'Pending Cancel', color: 'warning' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
};

const createMetaGetter =
  (map, { defaultLabel = 'Unknown', defaultColor = 'default' } = {}) =>
  (value) => {
    const key = normalizeStatusKey(value);
    if (!key) {
      return { label: '', color: 'default', key: null };
    }
    const meta = map[key];
    if (meta) {
      return {
        label: meta.label ?? defaultLabel,
        color: meta.color ?? defaultColor,
        key,
      };
    }
    return {
      label: toTitleCase(value) || defaultLabel,
      color: defaultColor,
      key,
    };
  };

export const formatStatusLabel = (value) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return toTitleCase(value);
};

export const getEnrollmentStatusMeta = createMetaGetter(ENROLLMENT_STATUS_MAP, {
  defaultLabel: 'Unknown',
});

export const getApiPresenceStatusMeta = createMetaGetter(API_PRESENCE_STATUS_MAP, {
  defaultLabel: 'Unknown',
});

export const getProductSelectionStatusMeta = createMetaGetter(PRODUCT_SELECTION_STATUS_MAP, {
  defaultLabel: 'Unknown',
});

export const getHasLiveSelectionsMeta = createMetaGetter(HAS_LIVE_SELECTIONS_MAP, {
  defaultLabel: 'No',
});

export const getSelectionLifecycleStatusMeta = createMetaGetter(PRODUCT_SELECTION_LIFECYCLE_MAP, {
  defaultLabel: 'Unknown',
});

export const getBooleanStatusMeta = (value) =>
  createMetaGetter(HAS_LIVE_SELECTIONS_MAP, { defaultLabel: 'No' })(value);

export const getBillingStatusMeta = createMetaGetter(IS_BILLING_STATUS_MAP, {
  defaultLabel: 'Not Billing',
});

export const getTerminationStatusMeta = createMetaGetter(IS_TERMINATED_STATUS_MAP, {
  defaultLabel: 'Active',
});

export const getConsentStatusMeta = createMetaGetter(CONSENT_STATUS_MAP, {
  defaultLabel: 'Incomplete',
});

const coerceNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
};

/**
 * Status meta helper for Action Required countdown fields.
 */
export const getDaysRemainingMeta = (daysRemaining, thresholdDays, { criticalDays = 10 } = {}) => {
  const remaining = coerceNumber(daysRemaining);
  const threshold = coerceNumber(thresholdDays);
  if (remaining === null || threshold === null || threshold <= 0) {
    return { label: remaining === null ? '—' : String(remaining), color: 'default', key: null };
  }
  if (remaining <= criticalDays) return { label: String(remaining), color: 'error', key: 'CRITICAL' };
  if (remaining <= Math.ceil(threshold / 2)) return { label: String(remaining), color: 'warning', key: 'WARNING' };
  return { label: String(remaining), color: 'success', key: 'OK' };
};

const joinWithAnd = (items = []) => {
  const list = items.filter((item) => String(item || '').trim());
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
};

export const getActionRequiredReasonData = (row) => {
  if (!row) return null;
  const reason = row.reason && typeof row.reason === 'string'
    ? (() => { try { return JSON.parse(row.reason); } catch { return null; } })()
    : row.reason;
  if (!reason || typeof reason !== 'object') return null;
  return reason;
};

/**
 * Format a friendlier Action Required reason summary.
 */
export const formatActionRequiredReasonSummary = (row) => {
  if (!row) return '';
  const reason = getActionRequiredReasonData(row);
  const enrollment = row.currentEnrollmentData || row.currentEnrollment || null;
  const selections = Array.isArray(enrollment?.productSelections) ? enrollment.productSelections : [];
  if (!reason && selections.length === 0) return '';

  const hasPhrases = [];
  const isPhrases = [];
  const isIncomplete = (value) => {
    const key = normalizeStatusKey(value);
    if (!key) return false;
    if (key === 'NA' || key === 'N_A' || key === 'NOT_APPLICABLE') return false;
    return key !== 'COMPLETE';
  };

  const rawSteps = Array.isArray(reason?.incompleteSteps) ? reason.incompleteSteps : [];
  const normalizedSteps = rawSteps
    .map((step) => normalizeStatusKey(step))
    .filter(Boolean);
  const stepSet = new Set(normalizedSteps);
  const coveredKeys = new Set();

  if (isIncomplete(reason?.programConsentStatusCd) && !stepSet.has('PROGRAM_CONSENT')) {
    isPhrases.push('program consent is incomplete');
    coveredKeys.add('PROGRAM_CONSENT');
  }
  if (isIncomplete(reason?.dataConsentStatusCd) && !stepSet.has('DATA_CONSENT')) {
    isPhrases.push('data consent is incomplete');
    coveredKeys.add('DATA_CONSENT');
  }
  if (isIncomplete(reason?.programSelectionStatusCd) && !stepSet.has('PROGRAM_SELECTION')) {
    isPhrases.push('program selection is incomplete');
    coveredKeys.add('PROGRAM_SELECTION');
  }
  if (
    normalizeStatusKey(reason?.productSelectionStatusCd) === 'INCOMPLETE' &&
    !stepSet.has('PRODUCT_SELECTION')
  ) {
    isPhrases.push('product selection is incomplete');
    coveredKeys.add('PRODUCT_SELECTION');
  }
  if (selections.length === 0 && !coveredKeys.has('PRODUCT_SELECTION')) {
    hasPhrases.push('no selections');
  }
  // Intentionally omit "missing steps" from the friendly summary.

  if (hasPhrases.length && isPhrases.length) {
    return `Dealer has ${joinWithAnd(hasPhrases)} and ${joinWithAnd(isPhrases)}`;
  }
  if (hasPhrases.length) {
    return `Dealer has ${joinWithAnd(hasPhrases)}`;
  }
  if (isPhrases.length) {
    return `Dealer ${joinWithAnd(isPhrases)}`;
  }
  return '';
};
